import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Package, Calendar, Building, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Order } from '@/hooks/useOrdersManagement/types';

interface StaffProductsHeaderProps {
  order: Order;
  onQuickAdd?: () => void;
}

export const StaffProductsHeader = ({ order, onQuickAdd }: StaffProductsHeaderProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/staff-dashboard');
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200',
      archived: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium border rounded-full ${styles[status as keyof typeof styles] || styles.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-3 md:p-4 mb-4 md:mb-6">
      <div className="flex flex-col gap-3 md:gap-4">
        {/* Header Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <Button variant="outline" onClick={handleBack} size="sm" className="shrink-0">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Back to Dashboard</span>
              <span className="sm:hidden ml-1">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                Order Products
              </h1>
              <p className="text-xs md:text-sm text-gray-600 truncate">
                Quick add and view scanned products
              </p>
            </div>
          </div>
          {onQuickAdd && (
            <Button onClick={onQuickAdd} size="sm" className="shrink-0">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Quick Add</span>
              <span className="sm:hidden ml-1">Add</span>
            </Button>
          )}
        </div>

        {/* Order Information */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-900">{order.supplierName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">{formatDate(order.deliveryDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">{order.productCount} products</span>
            </div>
            {getStatusBadge(order.status)}
          </div>
          
          <div className="text-xs text-gray-500 font-mono">
            Order ID: {order.id}
          </div>
        </div>
      </div>
    </div>
  );
};